import { useTranslation } from 'react-i18next';
import {
  InputProjectSimulator,
  MaterialsToComplete,
  SelectProjectSimulator,
} from '../../ProjectSimulator';
import StairsSVG from '../../../../../assets/images/projectSimulator/StairsSVG';
import ElevatorSVG from '../../../../../assets/images/projectSimulator/ElevatorSVG';
import { MaterialSynthese } from '../../../../projects/EquipmentList/AddEquipment/ModelingEquipment/CarbonEstimation/CarbonEstimation';
import { Controller } from 'react-hook-form';
import { Checkbox } from '@material-ui/core';
import { UseFormReturn } from 'react-hook-form';
import { BuildingStructureData } from '../BuildingStructure';
import { ListValueProps, projectSimulatorFieldRules } from '../../utils';

type BuildingStructureFirstPartProps = {
  buildingStructure: BuildingStructureData;
  frameworkMaterials: MaterialsToComplete[];
  form: UseFormReturn;
};

function BuildingStructureFirstPart({
  buildingStructure,
  frameworkMaterials,
  form,
}: BuildingStructureFirstPartProps) {
  const { t } = useTranslation();

  const slabList: ListValueProps[] = Object.values(buildingStructure.slab.list);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '12px',
        width: '50%',
        paddingLeft: '24px',
        borderRight: '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <label className="form-label">
          {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.FUNDATION', {
            defaultValue: 'Type de fondations',
          })}
        </label>
        <SelectProjectSimulator
          form={form}
          name="foundationType"
          disabled={true}
          defaultValue={buildingStructure.foundationType.value}
          listValues={buildingStructure.foundationType.list.TRADITIONNAL}
        />
      </div>

      <div>
        <label className="form-label">
          {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.FRAMEWORK', {
            defaultValue: 'Ossature',
          })}
        </label>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            rowGap: '6px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignSelf: 'start',
              columnGap: '10px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                rowGap: '4px',
              }}
            >
              <InputProjectSimulator
                form={form}
                name="wood"
                defaultValue={buildingStructure.framework.wood}
                rules={projectSimulatorFieldRules.percentage}
                isPercentageField={true}
                style={{ width: '67px' }}
              />
              <label className="form-field--label">
                {t(
                  'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.FIELD.LABEL.WOOD',
                  { defaultValue: 'Bois' }
                )}
              </label>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                rowGap: '4px',
              }}
            >
              <InputProjectSimulator
                form={form}
                name="concrete"
                defaultValue={buildingStructure.framework.concrete}
                rules={projectSimulatorFieldRules.percentage}
                isPercentageField={true}
                style={{ width: '67px' }}
              />
              <label className="form-field--label">
                {t(
                  'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.FIELD.LABEL.CONCRETE',
                  { defaultValue: 'Béton' }
                )}
              </label>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                rowGap: '4px',
              }}
            >
              <InputProjectSimulator
                form={form}
                name="steel"
                defaultValue={buildingStructure.framework.steel}
                rules={projectSimulatorFieldRules.percentage}
                isPercentageField={true}
                style={{ width: '67px' }}
              />
              <label className="form-field--label">
                {t(
                  'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.FIELD.LABEL.STEEL',
                  { defaultValue: 'Acier' }
                )}
              </label>
            </div>
          </div>
          <div className="ossature-container">
            <MaterialSynthese lines={frameworkMaterials} />
          </div>
        </div>
      </div>

      <Controller
        control={form.control}
        name="reinforcedConcreteStructure"
        defaultValue={buildingStructure.reinforcedConcreteStructure}
        render={({ field }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              {...field}
              color="primary"
              checked={field.value ?? buildingStructure.reinforcedConcreteStructure}
              value={field.value ?? buildingStructure.reinforcedConcreteStructure}
            />
            <p className="MuiFormControlLabel-label">
              {t(
                'APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.CHECKBOX.LABEL.REINFORCED_CONCRETE_STRUCTURE',
                {
                  defaultValue: 'Structure voile béton',
                }
              )}
            </p>
          </div>
        )}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <label className="form-label">
          {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.SLAB', {
            defaultValue: 'Dalle',
          })}
        </label>
        <SelectProjectSimulator
          disabled={true}
          form={form}
          name="slab"
          listValues={slabList[0]}
          defaultValue={buildingStructure.slab.value}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <label className="form-label">Déplacements intérieurs</label>
        <div
          style={{
            display: 'flex',
            columnGap: '32px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '25%',
            }}
          >
            <label className="form-label">
              {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.ELEVATORS', {
                defaultValue: 'Ascenseurs',
              })}
            </label>
            <InputProjectSimulator
              form={form}
              name="elevators"
              defaultValue={buildingStructure.elevators}
              style={{ maxWidth: '100%' }}
            />
            <div style={{ marginBottom: '16px' }} />
            <ElevatorSVG />
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '25%',
            }}
          >
            <label className="form-label">
              {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.STAIRS', {
                defaultValue: 'Escaliers',
              })}
            </label>
            <InputProjectSimulator
              form={form}
              name="stairs"
              defaultValue={buildingStructure.elevators}
              style={{ maxWidth: '100%' }}
            />
            <div style={{ marginBottom: '16px' }} />
            <StairsSVG />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <label className="form-label">
          {t('APP.PROJECT.SIMULATOR.ADVANCED_CONCEPTION.FORM.LABEL.ROOF_MATERIAL', {
            defaultValue: 'Couverture',
          })}
        </label>
        <SelectProjectSimulator
          form={form}
          name="roofMaterial"
          disabled={true}
          defaultValue={buildingStructure.roofMaterial.value}
          listValues={buildingStructure.roofMaterial.list.METAL}
        />
      </div>
    </div>
  );
}

export default BuildingStructureFirstPart;
